import { useSelector, shallowEqual, useDispatch } from "react-redux";
import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Modal } from "semantic-ui-react";
import CloseImg from "assets/images/nav/close.svg";
import PlusImg from "assets/images/nav/plus.svg";
import CheckImg from "assets/images/nav/check.svg";
import { SET_NEW_FILTERS, SET_SELECTED_DATE_FILTER } from "constants/actions";
import SurveyVersions from "reports/Audit/VersionControl";
import { useLocation } from "react-router-dom";
import { filterStaplesOptions } from "utilities/organizationUtils";
import { filter_data } from "reports/Tag/calculations";
import { usePageTranslations } from 'hooks/usePageTranslations';
import { useLanguage } from 'hooks/useLanguage';
import { transformCategoriesToFrench } from 'utilities/outcomeQTranslations';


function getFactors(questions, language = 'en') {
  if (!questions) return [];

  // In some contexts `questions` may be an array, in others an object
  const source = Array.isArray(questions) ? questions[0] : questions;

  if (!source || !Array.isArray(source.dimensions)) {
    return [];
  }

  const factors = source.dimensions.map((dimension, dimensionIndex) => ({
    dimension: (language === 'fr' && dimension.fr && dimension.fr.length > 0) ? dimension.fr : dimension.title,
    factors:
      (dimension.factors || []).map((factor) => ({
        title: (language === 'fr' && factor.fr && factor.fr.length > 0) ? factor.fr : factor.title,
        id: factor.id - 1,
        type_of: "factor",
        dimensionIndex,
      })) || [],
  }));

  return factors;
}

// Helper function to process outcome filtering logic
function processOutcomeFilters(selectedFilters) {
  // Get outcome filters from selectedFilters
  const outcomeFilters =
    selectedFilters?.filter((filter) => filter.type_of === "outcome") || [];

  // Check if we're filtering by factors only (no outcomes should be calculated)
  const hasFactorFilters = selectedFilters?.some(
    (filter) => filter.type_of === "factor"
  );
  const shouldIncludeOutcomes = !hasFactorFilters || outcomeFilters.length > 0;

  return {
    outcomeFilters,
    hasFactorFilters,
    shouldIncludeOutcomes,
  };
}

// Export the outcome filtering logic for use by other components
export { processOutcomeFilters };
// Helper function to check if we're in OTP context
function isOTPContext(pathname) {
  return pathname.includes('/app/otp/');
}

// Helper function to get category structure for rendering
// In OTP context, creates sections for both parent categories and their subfactors
function getCategoryStructure(category, isOTP) {
  if (!isOTP) {
    return [{
      title: category.name,
      options: category.options || [],
      isMainCategory: true
    }];
  }

  const sections = [];

  // First, add the main category section with all options (including those with subfactors)
  sections.push({
    title: category.name,
    options: category.options || [],
    isMainCategory: true
  });

  // Then, for each option that has subfactors, create a separate section
  const optionsWithSubfactors = category.options?.filter(option => option.subfactors && option.subfactors.length > 0) || [];

  optionsWithSubfactors.forEach(option => {
    sections.push({
      title: option.name,
      options: option.subfactors.map(subfactor => ({
        ...subfactor,
        parentOption: option.name,
        isSubfactor: true
      })),
      isSubfactorSection: true,
      parentCategory: category
    });
  });

  return sections;
}

const Filters = ({
  outcomes,
  onSelectOutcomes,
  filters,
  hideFactors = true,
}) => {
  const t = usePageTranslations('app/audit/simple/Filters/translations');
  const { language } = useLanguage();
  const dispatch = useDispatch();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrientation, setSelectedOrientation] = useState(null);
  const [dimensions, setDimensions] = useState([]);
  const selectedFilters = useSelector(
    (state) => state.audit?.new_filters,
    shallowEqual
  );
  const [selectedDate, setSelectedDate] = useState(null);
  // Local state for outcomes instead of Redux
  const [localOutcomeFilters, setLocalOutcomeFilters] = useState([]);

  const categoryRefs = useRef({});
  const mainContentRef = useRef(null);

  // Check if we're in OTP context
  const isOTP = isOTPContext(location.pathname);

  const { core_data, dates, selectedDateFilter, originalDates, get_employee_permission, get_auth, get_selectedOrg, raw_data } = useSelector(
    (state) => ({
      core_data: state.audit?.core_data,
      get_survey_structure: state.surveystructure.survey_structure,
      dates: state.audit?.dates,
      selectedDateFilter: state.audit?.selectedDateFilter,
      originalDates: state.audit?.originalDates,
      get_employee_permission: state.employee_permission,
      get_auth: state.auth,
      get_selectedOrg: state.selectedOrg,
      raw_data: state.audit?.raw_data,
    }),
    shallowEqual
  );

  const ambassadorId = useSelector(
    (state) => Number(state.auth?.ambassador_id),
    shallowEqual
  );

  // Build permission info for filtering
  const employeeId = get_auth?.employee_id;
  const permissionInfo = {
    permissionRecords: get_employee_permission?.employee_permission,
    employeeId,
    employee: { account_type: get_auth?.account_type },
  };

  // Apply filterStaplesOptions to categories
  const baseFilteredCategories = filterStaplesOptions(
    core_data?.categories?.[0]?.categories,
    permissionInfo
  );

  // Transform categories to French if language is set to French
  const filteredCategories = React.useMemo(() => {
    if (language === 'fr' && baseFilteredCategories) {
      const transformed = transformCategoriesToFrench(baseFilteredCategories);
      // DEBUG: French translation applied to categories
      return transformed;
    }
    return baseFilteredCategories;
  }, [baseFilteredCategories, language]);


  useEffect(() => {
    // Set selected date from Redux if available
    if (selectedDateFilter) {
      setSelectedDate(selectedDateFilter);
    } else {
      setSelectedDate(null);
    }
  }, [selectedDateFilter]);

  const handleSelect = (item, parent) => {
    const selectedItem = {
      ...item,
      parent: parent?.id || parent?.name,
      id: item?.id === 0 ? 0 : item.id || item.key || item.value || item.name,
    };

    // Local multi-select for Outcomes
    if (selectedItem.parent === "Outcomes") {
      const isSelected = localOutcomeFilters.some(
        (f) => f.id === selectedItem.id
      );
      const updatedLocal = isSelected
        ? localOutcomeFilters.filter((f) => f.id !== selectedItem.id)
        : [...localOutcomeFilters, selectedItem];
      setLocalOutcomeFilters(updatedLocal);
      onSelectOutcomes && onSelectOutcomes(updatedLocal);
      return;
    }

    // --- 3. Everything Else => normal selectedFilters (existing logic) ---
    const isSelected = selectedFilters.some(
      (i) => i.id === selectedItem.id && i.parent === selectedItem.parent
    );

    const updatedFilters = isSelected
      ? selectedFilters.filter(
          (i) => !(i.id === selectedItem.id && i.parent === selectedItem.parent)
        )
      : [...selectedFilters, selectedItem];

    dispatch({
      type: SET_NEW_FILTERS,
      payload: updatedFilters,
    });
  };

  // Handle date selection
  const handleDateSelect = (dateValue) => {
    if (dateValue === "latest") {
      // "Latest" means no filter
      setSelectedDate(null);
      dispatch({
        type: SET_SELECTED_DATE_FILTER,
        payload: null,
      });
    } else {
      // Store as ISO string for consistency
      const dateObj = new Date(dateValue);
      const formattedDate = !isNaN(dateObj.getTime())
        ? dateObj.toISOString()
        : dateValue;

      setSelectedDate(formattedDate);
      dispatch({
        type: SET_SELECTED_DATE_FILTER,
        payload: formattedDate,
      });
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return t('latest');

    // If it's already a properly formatted string, return it
    if (
      typeof dateString === "string" &&
      !dateString.includes("GMT") &&
      !dateString.includes("T")
    ) {
      return dateString;
    }

    // Otherwise convert to a formatted date string
    try {
      const date = new Date(dateString);
      // Check if date is valid before formatting
      if (isNaN(date.getTime())) {
        console.error("Invalid date:", dateString);
        return String(dateString);
      }

      // Format with month name, day, and year
      const options = { year: "numeric", month: "short", day: "numeric" };
      return date.toLocaleDateString(undefined, options);
    } catch (e) {
      console.error("Error formatting date:", e);
      return String(dateString); // Fallback to stringify
    }
  };

  useEffect(() => {
    if (core_data?.questions) {
      setDimensions(getFactors(core_data?.questions, language));
    }
  }, [core_data?.questions, language]);

  /**
   * clearAll
   * Reset everything, including multi sentiments
   */
  const clearAll = () => {
    // Clear orientation
    setSelectedOrientation(null);

    // Clear local outcome filters
    setLocalOutcomeFilters([]);
    onSelectOutcomes && onSelectOutcomes([]);

    // Clear date filter
    setSelectedDate(null);
    dispatch({
      type: SET_SELECTED_DATE_FILTER,
      payload: null,
    });

    // Clear selectedFilters from redux
    dispatch({ type: SET_NEW_FILTERS, payload: [] });
  };

  useEffect(() => {
    const refs = {};

    if (outcomes) {
      refs["Outcomes"] = categoryRefs.current["Outcomes"] || React.createRef();
      outcomes.forEach((outcome) => {
        refs[outcome.id] = categoryRefs.current[outcome.id] || React.createRef();
      });
    }

    refs["DateFilter"] = categoryRefs.current["DateFilter"] || React.createRef();
    refs["CategoriesSection"] = categoryRefs.current["CategoriesSection"] || React.createRef();
    refs["TagsSection"] = categoryRefs.current["TagsSection"] || React.createRef();
    if (filteredCategories) {
      filteredCategories.forEach((category) => {
        const sections = getCategoryStructure(category, isOTP);
        sections.forEach((section, sectionIndex) => {
          const sectionKey = section.isSubfactorSection
            ? `${category.id}-${section.title}`
            : category.id;

          if (sectionIndex === 0) {
            // Main category uses the original category.id - reuse existing ref if available
            refs[category.id] = categoryRefs.current[category.id] || React.createRef();
          } else {
            // Subfactor sections use the composite key - reuse existing ref if available
            refs[sectionKey] = categoryRefs.current[sectionKey] || React.createRef();
          }
        });
      });
    }

    // Add refs for dimensions - reuse existing refs if available
    dimensions.forEach((dimension) => {
      refs[dimension.dimension] = categoryRefs.current[dimension.dimension] || React.createRef();
    });

    categoryRefs.current = refs;
  }, [filteredCategories, outcomes, dimensions, isOTP]);

  const handleCategoryClick = (categoryId) => {
    const ref = categoryRefs.current[categoryId]?.current;

    if (ref && mainContentRef.current) {
      const mainContent = mainContentRef.current;
      // Get the position of the element relative to the scroll container
      const containerRect = mainContent.getBoundingClientRect();
      const elementRect = ref.getBoundingClientRect();
      const relativeTop = elementRect.top - containerRect.top;
      const scrollPosition = mainContent.scrollTop + relativeTop - 20;

      mainContent.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  // Intelligent search filter logic - filters both category names and option values
  const getFilteredCategories = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return filteredCategories;
    }

    const lowerSearch = searchTerm.toLowerCase();

    return filteredCategories?.map(cat => {
      const sections = getCategoryStructure(cat, isOTP);

      // Filter sections based on search
      const filteredSections = sections
        .map(section => {
          // Check if section name matches
          const sectionMatches = section.title.toLowerCase().includes(lowerSearch);

          // Filter options that match
          const matchingOptions = section.options?.filter(opt =>
            opt.name.toLowerCase().includes(lowerSearch)
          ) || [];

          // Include section if name matches OR has matching options
          if (sectionMatches || matchingOptions.length > 0) {
            return {
              ...section,
              options: sectionMatches ? section.options : matchingOptions
            };
          }

          return null;
        })
        .filter(Boolean);

      // Only include category if it has matching sections
      if (filteredSections.length > 0) {
        return {
          ...cat,
          filteredSections
        };
      }

      return null;
    }).filter(Boolean) || [];
  }, [filteredCategories, searchTerm, isOTP]);

  // Use originalDates if available, otherwise fallback to dates
  const availableDates = originalDates || dates;

  // Calculate unique respondent count based on selected filters
  const respondentCount = React.useMemo(() => {
    // Get the appropriate data source based on date filter
    // If a specific date is selected, we might need to use raw_data[0].last
    // For now, use responses as the primary source
    const dataSource = raw_data?.[0]?.responses;
    if (!dataSource || !Array.isArray(dataSource)) {
      return 0;
    }

    // Combine selectedFilters and localOutcomeFilters for filtering
    const allFilters = [...(selectedFilters || []), ...localOutcomeFilters];

    // If no filters are selected, return total count
    if (allFilters.length === 0 && !selectedDate) {
      const uniqueEmployees = new Set();
      dataSource.forEach((response) => {
        // Response might be an object with employee field, or might be nested
        const employee = response?.employee || response?.data?.employee;
        if (employee) {
          uniqueEmployees.add(employee);
        }
      });
      return uniqueEmployees.size;
    }

    // Filter data based on selected filters
    const filteredData = filter_data(dataSource, allFilters);

    // Handle date filtering if selected (date filtering might be handled at data level)
    // For now, we'll count based on filtered data only
    // If date filtering affects the count, it's likely handled upstream

    // Count unique respondents (by employee field)
    const uniqueEmployees = new Set();
    if (Array.isArray(filteredData)) {
      filteredData.forEach((response) => {
        // Handle both direct response objects and potentially nested structures
        const employee = response?.employee || response?.data?.employee;
        if (employee) {
          uniqueEmployees.add(employee);
        }
      });
    }

    return uniqueEmployees.size;
  }, [raw_data, selectedFilters, localOutcomeFilters, selectedDate]);

  return (
    <FilterComponent>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
          marginBottom: "5px",
          marginTop: "5px",
        }}
      >
        {/* Show selected date filter if one is selected */}
        {selectedDate && (
          <SelectedOption onClick={() => handleDateSelect("latest")}>
            {t('date')} {formatDate(selectedDate)} <IconImg src={CloseImg} />
          </SelectedOption>
        )}

        {/* Render local outcome filters first */}
        {localOutcomeFilters.map((item, index) => (
          <SelectedOption
            key={`local-outcome-${index}`}
            onClick={() => handleSelect(item, { id: "Outcomes" })}
          >
            {item.name} <IconImg src={CloseImg} />
          </SelectedOption>
        ))}

        {/* Render all selected filters (outcomes no longer included here) */}
        {selectedFilters.map((item, index) => (
          <SelectedOption
            key={index}
            onClick={() => handleSelect(item, { id: item.parent })}
          >
            {item.name} <IconImg src={CloseImg} />
          </SelectedOption>
        ))}

        {/* The "Add Filters" button */}
        <Container onClick={() => setOpen(true)}>
          {selectedFilters.length === 0 && !selectedDate ? (
            <>
              <IconImg2 src={PlusImg} /> {t('addFilters')}
            </>
          ) : (
            <>
                <IconImg2 src={PlusImg} /> {t('filters')} (
              {selectedFilters.length + (selectedDate ? 1 : 0)})
            </>
          )}
        </Container>

        {/* If anything is selected, show a "Clear All" */}
        {(selectedFilters.length > 0 ||
          selectedOrientation ||
          selectedDate) && <ClearAll onClick={clearAll}>{t('clearAll')}</ClearAll>}
      </div>

      <Modal
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        open={open}
        closeIcon={true}
      >
        <Modal.Header>
          <HeaderContent>
            <div>
              <ModalTitle>{t('filters')}</ModalTitle>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <SurveyVersions label={true} />
            </div>
          </HeaderContent>
        </Modal.Header>
        <SearchBarContainer>
          <SearchBar
            type="text"
            placeholder={t('search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBarContainer>
        <Content>
          <SideMenuContainer>
            <SideMenu>
              <CategoryTitle>{t('filterByDate')}</CategoryTitle>
              <Item onClick={() => handleCategoryClick("DateFilter")}>
                {t('dateFilter')}
              </Item>
              <CategoryTitle onClick={() => handleCategoryClick("CategoriesSection")}>
                {t('filterByCategories')}
              </CategoryTitle>
              <CategoryTitle>{t('filterByTags')}</CategoryTitle>
              {getFilteredCategories?.map((category) => {
                const sections = category.filteredSections || getCategoryStructure(category, isOTP);

                return sections.map((section, sectionIndex) => {
                  const sectionKey = section.isSubfactorSection
                    ? `${category.id}-${section.title}`
                    : category.id;

                  return (
                    <Item
                      key={sectionKey}
                      onClick={() => handleCategoryClick(sectionIndex === 0 ? category.id : sectionKey)}
                    >
                      {section.title}
                    </Item>
                  );
                });
              })}
              {/* {!hideFactors && (
                <>
                  <CategoryTitle>Filter by dimension</CategoryTitle>
                  {dimensions.map((dimension, index) => (
                    <Item
                      key={index}
                      onClick={() => handleCategoryClick(dimension.dimension)}
                    >
                      {dimension.dimension}
                    </Item>
                  ))}
                </>
              )} */}
            </SideMenu>
          </SideMenuContainer>

          <Separator />

          <MainMenuContainer ref={mainContentRef}>
            <MainMenu>
              {/* Date Filter Section */}
              <div ref={categoryRefs.current["DateFilter"]}>
                <Section>
                  <SectionTitle>{t('dateFilter')}</SectionTitle>
                  <DateFilterSection>
                    <DateOptions>
                      <DateOption
                        selected={selectedDate === null}
                        onClick={() => handleDateSelect("latest")}
                      >
                        {selectedDate === null && <IconImg2 src={CheckImg} />}{" "}
                        {t('latest')}
                      </DateOption>
                      {availableDates &&
                        availableDates.map((date, index) => (
                          <DateOption
                            key={index}
                            selected={
                              selectedDate &&
                              date &&
                              (selectedDate === date ||
                                new Date(selectedDate).getTime() ===
                                  new Date(date).getTime())
                            }
                            onClick={() => handleDateSelect(date)}
                          >
                            {selectedDate &&
                              date &&
                              (selectedDate === date ||
                                new Date(selectedDate).getTime() ===
                                  new Date(date).getTime()) && (
                                <IconImg2 src={CheckImg} />
                              )}{" "}
                            {formatDate(date)}
                          </DateOption>
                        ))}
                    </DateOptions>
                  </DateFilterSection>
                </Section>
              </div>

              <Section ref={categoryRefs.current["CategoriesSection"]}>
                <SectionTitle>{t('filterByCategories')}</SectionTitle>

                {outcomes && (
                  <div ref={categoryRefs.current["Outcomes"]}>
                    <OutcomesSection>
                      {outcomes &&
                        outcomes
                          .filter((outcome) =>
                            outcome.name
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          )
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((outcome) => (
                            <OutcomeOptions key={outcome.id}>
                              <Option
                                onClick={() =>
                                  handleSelect(outcome, {
                                    id: "Outcomes",
                                    name: outcome.name,
                                  })
                                }
                                selected={localOutcomeFilters.some(
                                  (f) => f.id === outcome.id
                                )}
                              >
                                {localOutcomeFilters.some(
                                  (f) => f.id === outcome.id
                                ) && <IconImg2 src={CheckImg} />}{" "}
                                {outcome.name}
                              </Option>
                            </OutcomeOptions>
                          ))}
                    </OutcomesSection>
                  </div>
                )}

                {/* Render dimensions and their factors */}
                {!hideFactors &&
                  dimensions.map((dimension, index) => (
                    <div
                      key={index}
                      ref={categoryRefs.current[dimension.dimension]}
                    >
                      {/* <Title>{dimension.dimension}</Title> */}
                      <Options>
                        {dimension.factors
                          .filter((factor) =>
                            factor.title
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          )
                          .sort((a, b) => a.title.localeCompare(b.title))
                          .map((factor) => (
                            <Option
                              key={factor.id}
                              onClick={() =>
                                handleSelect(
                                  {
                                    id: factor.id,
                                    name: factor.title,
                                    dimensionIndex: index,
                                    type_of: "factor",
                                  },
                                  {
                                    id: dimension.dimension,
                                    name: dimension.dimension,
                                  }
                                )
                              }
                              selected={selectedFilters.some(
                                (item) =>
                                  item.id === factor.id &&
                                  item.parent === dimension.dimension &&
                                  item.type_of === "factor" &&
                                  item.dimensionIndex === index
                              )}
                            >
                              {selectedFilters.some(
                                (item) =>
                                  item.id === factor.id &&
                                  item.parent === dimension.dimension &&
                                  item.type_of === "factor" &&
                                  item.dimensionIndex === index
                              ) && <IconImg2 src={CheckImg} />}{" "}
                              {factor.title}
                            </Option>
                          ))}
                      </Options>
                    </div>
                  ))}
              </Section>

              <Section ref={categoryRefs.current["TagsSection"]}>
                <SectionTitle>{t('filterByTags')}</SectionTitle>

                {getFilteredCategories?.map((category) => {
                  const sections = category.filteredSections || getCategoryStructure(category, isOTP);

                  return sections.map((section, sectionIndex) => {
                    const sectionKey = section.isSubfactorSection
                      ? `${category.id}-${section.title}`
                      : category.id;

                    return (
                      <div
                        key={sectionKey}
                        ref={sectionIndex === 0 ? categoryRefs.current[category.id] : categoryRefs.current[sectionKey]}
                      >
                        <Title>{section.title}</Title>
                        <Options>
                          {section.options?.sort((a, b) => a.name.localeCompare(b.name)).map((option) => {
                              // Determine the parent for filtering
                              let parentForFiltering;
                              if (section.isSubfactorSection) {
                                // For subfactor sections, use the subfactor section title as parent
                                parentForFiltering = section.title;
                              } else {
                                // For main category, use category id
                                parentForFiltering = category.id;
                              }

                              return (
                                <Option
                                  key={option.id}
                                  onClick={() => handleSelect(option, {
                                    id: parentForFiltering,
                                    name: section.title
                                  })}
                                  selected={selectedFilters.some(
                                    (item) =>
                                      item.id === option.id &&
                                      item.parent === parentForFiltering
                                  )}
                                >
                                  {selectedFilters.some(
                                    (item) =>
                                      item.id === option.id &&
                                      item.parent === parentForFiltering
                                  ) && <IconImg2 src={CheckImg} />}{" "}
                                  {option.name}
                                </Option>
                              );
                            })}
                        </Options>
                      </div>
                    );
                  });
                })}
              </Section>
            </MainMenu>
          </MainMenuContainer>
        </Content>
        <Bottom>
          <RespondentCountContainer>
            <RespondentCountText>
              {t('respondentsSelected', { count: respondentCount, plural: respondentCount !== 1 ? 's' : '' })}
            </RespondentCountText>
            <InfoWrapper tabIndex="0" aria-label="Respondent count info">
              <InfoIcon aria-hidden="true">i</InfoIcon>
              <Tooltip role="tooltip">
                {t('respondentCountTooltip')}
              </Tooltip>
            </InfoWrapper>
          </RespondentCountContainer>
          <Button onClick={() => setOpen(false)}>{t('select')}</Button>
        </Bottom>
      </Modal>
    </FilterComponent>
  );
};

export default Filters;

// Styled components
const Container = styled.div`
  color: #2d70e2;
  font-family: Raleway;
  font-size: 12px;
  font-style: normal;
  line-height: 150%;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 0px;
`;

const SideMenuContainer = styled.div`
  width: 200px;
  overflow-y: auto;
  max-height: calc(100vh - 290px);

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent; /* Chrome/Safari/Webkit */
  }
`;

const SideMenu = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const MainMenuContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 290px);

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent; /* Chrome/Safari/Webkit */
  }
`;

const Item = styled.div`
  margin-bottom: 5px;
  min-height: 36px;
  padding: 8px 12px 8px 27px;
  color: #666d79;
  font-family: Raleway;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  line-height: 1.4;
`;

const MainMenu = styled.div`
  padding: 20px;
`;

const Content = styled.div`
  width: 100%;
  display: flex;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const OutcomesSection = styled.div`
  display: flex;
  flex-direction: row;
`;

const Options = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  font-family: Raleway;
  font-size: 14px;
  margin-bottom: 20px;
`;

const Option = styled.div`
  padding: 8px 16px;
  flex-direction: row;
  align-items: flex-start;
  border-radius: 30px;
  font-family: Raleway;
  font-size: 14px;
  margin-right: 10px;
  margin-bottom: 10px;
  background-color: ${(props) => (props.selected ? "#EBF1FD" : "#fff")};
  color: ${(props) => (props.selected ? "#2D70E2" : "#2A3039")};
  border: 1px solid ${(props) => (props.selected ? "#EBF1FD" : "#E5E5E5")};
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
`;


const SelectedOption = styled.div`
  margin-right: 10px;
  display: flex;
  align-items: center;
  font-family: Raleway;
  font-size: 12px;
  font-weight: 600;
  line-height: 140%;
  color: #2a3039;
  background-color: #f0f2f5;
  padding: 6px 12px;
  border-radius: 16px;
`;

const FilterComponent = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 85%;
`;

const IconImg = styled.img`
  width: 20px;
  margin-left: 3px;
`;

const IconImg2 = styled.img`
  width: 20px;
  margin-right: 4px;
  height: 20px;
`;


const OutcomeOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Button = styled.button`
  background: #2d70e2;
  border-radius: 8px;
  font-family: "Raleway";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  color: #ffffff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  cursor: pointer;
`;

const Bottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  width: 100%;
  border-top: 1px solid #e5e5e5;
  padding: 20px;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 10px;

  & > div:first-child {
    flex: 0 0 auto;
  }

  & > div:last-child {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 auto;
  }
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: bold;
`;


const SearchBarContainer = styled.div`
  padding: 10px 20px;
  background-color: #fff;
`;

const SearchBar = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  font-size: 14px;
`;

const Separator = styled.div`
  width: 1px;
  background-color: #e5e5e5;
  margin: 0 10px;
`;

const CategoryTitle = styled.div`
  font-weight: bold;
  margin-top: 20px;
  margin-bottom: 10px;
  font-family: Raleway;
  font-size: 14px;
  cursor: pointer; /* Made it clickable */
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 15px;
`;

const ClearAll = styled.div`
  color:#F15062;
  font-weight: 600;
  border: 1px solid #F2F2F2;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 5px;
  padding: 6px 12px;
`;

const DateFilterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const DateOptions = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  font-family: Raleway;
  font-size: 14px;
  margin-bottom: 20px;
`;

const DateOption = styled.div`
  padding: 8px 16px;
  flex-direction: row;
  align-items: flex-start;
  border-radius: 30px;
  font-family: Raleway;
  font-size: 14px;
  margin-right: 10px;
  margin-bottom: 10px;
  background-color: ${(props) => (props.selected ? "#EBF1FD" : "#fff")};
  color: ${(props) => (props.selected ? "#2D70E2" : "#2A3039")};
  border: 1px solid ${(props) => (props.selected ? "#EBF1FD" : "#E5E5E5")};
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
`;

const RespondentCountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: auto;
`;

const RespondentCountText = styled.span`
  font-family: Raleway;
  font-size: 14px;
  color: #2a3039;
  font-weight: 500;
`;

const InfoWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  color: #4b5563;
  background: #fff;
  font-size: 12px;
  cursor: default;
  outline: none;

  &:hover > div,
  &:focus > div {
    opacity: 1;
    visibility: visible;
    transform: translate(0, -4px);
  }
`;

const InfoIcon = styled.span`
  line-height: 1;
  font-weight: 700;
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translate(-50%, 4px);
  background: #111827;
  color: #f9fafb;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: normal;
  text-align: left;
  max-width: 280px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.08s ease, transform 0.08s ease;
  z-index: 2;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #111827;
  }
`;